// Datenzugriffe der Zeiterfassung. Die Rechenregeln liegen in berechnung.ts (vitest),
// hier stehen nur Abrufe und Schreibvorgänge.
import { supabase } from '../supabase/client';
import type { Arbeitszeitmodell, Stempel, Zuordnung } from './berechnung';
import type { Abwesenheit, AbwesenheitTyp } from './abwesenheit';
import type { TicketZeit } from './ticketzeit';
import type { Lohnart } from './lohnexport';

const STEMPEL_SPALTEN = 'id, mitarbeiter_id, beginn, ende, quelle, notiz, objekt_id, pausen:zeit_pausen(id, beginn, ende)';

export async function ladeModelle(): Promise<Arbeitszeitmodell[]> {
  const { data, error } = await supabase
    .from('arbeitszeit_modelle')
    .select('id, name, soll_minuten, urlaubstage_jahr, aktiv, nach_stunden')
    .order('name');
  if (error) { console.error('Arbeitszeitmodelle laden fehlgeschlagen', error.message); return []; }
  return (data || []) as Arbeitszeitmodell[];
}

export async function ladeZuordnungen(mitarbeiterId: string): Promise<Zuordnung[]> {
  const { data, error } = await supabase
    .from('mitarbeiter_arbeitszeit')
    .select('id, modell_id, gueltig_ab, start_saldo_minuten, urlaubstage_jahr, soll_minuten, nach_stunden')
    .eq('mitarbeiter_id', mitarbeiterId)
    .order('gueltig_ab');
  if (error) { console.error('Arbeitszeit-Zuordnung laden fehlgeschlagen', error.message); return []; }
  return (data || []) as Zuordnung[];
}

/** Stempel eines Zeitraums (Tagesgrenzen als 'YYYY-MM-DD', beide einschließlich). */
export async function ladeStempel(mitarbeiterId: string, vonTag: string, bisTag: string): Promise<Stempel[]> {
  const { data, error } = await supabase
    .from('zeit_stempel')
    .select(STEMPEL_SPALTEN)
    .eq('mitarbeiter_id', mitarbeiterId)
    .gte('beginn', `${vonTag}T00:00:00`)
    .lte('beginn', `${bisTag}T23:59:59`)
    .order('beginn');
  if (error) { console.error('Zeiten laden fehlgeschlagen', error.message); return []; }
  return (data || []) as Stempel[];
}

/** Der gerade laufende Abschnitt (ende is null) — es kann per Index nur einen geben. */
export async function offenerStempel(mitarbeiterId: string): Promise<Stempel | null> {
  const { data, error } = await supabase
    .from('zeit_stempel')
    .select(STEMPEL_SPALTEN)
    .eq('mitarbeiter_id', mitarbeiterId)
    .is('ende', null)
    .maybeSingle();
  if (error) { console.error('Laufenden Stempel laden fehlgeschlagen', error.message); return null; }
  return (data as Stempel) || null;
}

/**
 * Standort holen — nur wenn für diese Person eingeschaltet. Schlägt es fehl oder
 * lehnt der Browser ab, wird trotzdem gestempelt: eine Zeiterfassung, die ohne
 * GPS verweigert, ist im Keller oder Tiefgeschoss unbrauchbar.
 */
async function standortWennGewuenscht(mitarbeiterId: string): Promise<{ gps_breite: number; gps_laenge: number; gps_genauigkeit: number } | null> {
  const { data } = await supabase.from('profiles').select('gps_beim_stempeln').eq('id', mitarbeiterId).maybeSingle();
  if (!data?.gps_beim_stempeln || !navigator.geolocation) return null;
  try {
    const pos = await new Promise<GeolocationPosition>((res, rej) => {
      navigator.geolocation.getCurrentPosition(res, rej, { timeout: 8000, maximumAge: 60000, enableHighAccuracy: false });
    });
    return {
      gps_breite: Number(pos.coords.latitude.toFixed(6)),
      gps_laenge: Number(pos.coords.longitude.toFixed(6)),
      gps_genauigkeit: Math.round(pos.coords.accuracy),
    };
  } catch (e) {
    console.warn('Standort nicht verfügbar', (e as GeolocationPositionError)?.message);
    return null;
  }
}

export async function einstempeln(mitarbeiterId: string, objektId?: string | null): Promise<Stempel | null> {
  const standort = await standortWennGewuenscht(mitarbeiterId);
  const { data, error } = await supabase
    .from('zeit_stempel')
    .insert({
      mitarbeiter_id: mitarbeiterId, beginn: new Date().toISOString(), quelle: 'stempel',
      objekt_id: objektId || null, ...(standort || {}),
    })
    .select(STEMPEL_SPALTEN)
    .single();
  if (error) { console.error('Einstempeln fehlgeschlagen', error.message); return null; }
  return data as Stempel;
}

/** Feierabend: laufende Pause zuerst beenden, dann den Abschnitt schließen. */
export async function ausstempeln(stempel: Stempel): Promise<boolean> {
  const jetzt = new Date().toISOString();
  const offenePause = (stempel.pausen || []).find((p) => !p.ende);
  if (offenePause) await pauseBeenden(stempel.id);
  const { error } = await supabase.from('zeit_stempel').update({ ende: jetzt }).eq('id', stempel.id);
  if (error) { console.error('Ausstempeln fehlgeschlagen', error.message); return false; }
  return true;
}

export async function pauseStarten(stempelId: string): Promise<boolean> {
  const { error } = await supabase
    .from('zeit_pausen')
    .insert({ stempel_id: stempelId, beginn: new Date().toISOString() });
  if (error) { console.error('Pause starten fehlgeschlagen', error.message); return false; }
  return true;
}

export async function pauseBeenden(stempelId: string): Promise<boolean> {
  const { error } = await supabase
    .from('zeit_pausen')
    .update({ ende: new Date().toISOString() })
    .eq('stempel_id', stempelId)
    .is('ende', null);
  if (error) { console.error('Pause beenden fehlgeschlagen', error.message); return false; }
  return true;
}

/**
 * Nachtragen oder Korrigieren. Immer quelle 'manuell' — in der Übersicht ist dadurch
 * sichtbar, welche Zeiten nicht live gestempelt wurden.
 */
export async function stempelSpeichern(args: {
  id?: string;
  mitarbeiterId: string;
  beginn: string;
  ende: string | null;
  notiz?: string | null;
  pauseMinuten?: number;
}): Promise<boolean> {
  const satz = {
    mitarbeiter_id: args.mitarbeiterId,
    beginn: args.beginn,
    ende: args.ende,
    notiz: args.notiz || null,
    quelle: 'manuell',
  };
  let stempelId = args.id;
  if (stempelId) {
    const { error } = await supabase.from('zeit_stempel').update(satz).eq('id', stempelId);
    if (error) { console.error('Zeit speichern fehlgeschlagen', error.message); return false; }
  } else {
    const { data, error } = await supabase.from('zeit_stempel').insert(satz).select('id').single();
    if (error) { console.error('Zeit anlegen fehlgeschlagen', error.message); return false; }
    stempelId = data.id as string;
  }
  // Pause als EINE nachgetragene Zeile führen: erst alles weg, dann neu — sonst
  // müsste die Oberfläche einzelne Pausenzeilen verwalten.
  if (args.pauseMinuten !== undefined) {
    await supabase.from('zeit_pausen').delete().eq('stempel_id', stempelId);
    if (args.pauseMinuten > 0 && args.ende) {
      const start = new Date(args.beginn).getTime() + 4 * 60 * 60 * 1000; // Mitte des Vormittags
      const pBeginn = new Date(Math.min(start, new Date(args.ende).getTime() - args.pauseMinuten * 60000));
      const pEnde = new Date(pBeginn.getTime() + args.pauseMinuten * 60000);
      await supabase.from('zeit_pausen').insert({
        stempel_id: stempelId, beginn: pBeginn.toISOString(), ende: pEnde.toISOString(),
      });
    }
  }
  return true;
}

export async function stempelLoeschen(id: string): Promise<boolean> {
  const { error } = await supabase.from('zeit_stempel').delete().eq('id', id);
  if (error) { console.error('Zeit löschen fehlgeschlagen', error.message); return false; }
  return true;
}

// ── Verwaltung (Personal/Geschäftsführung) ───────────────────────────────────

/**
 * Die Belegschaft für Zeiterfassung, Abwesenheiten und Personalakte.
 * `ist_angestellt = false` blendet Entwickler- und reine Administrationszugänge aus —
 * deren RECHTE bleiben unberührt, sie tauchen nur nicht als Person in den Listen auf.
 */
export async function ladeMitarbeiter(): Promise<{ id: string; full_name: string | null; email: string | null; avatar_url?: string | null }[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, full_name, email, avatar_url, role, status, ist_angestellt')
    .in('role', ['admin', 'super admin', 'master admin', 'team', 'hausmeister'])
    .neq('ist_angestellt', false)
    .order('full_name');
  if (error) { console.error('Mitarbeiter laden fehlgeschlagen', error.message); return []; }
  return (data || []).filter((p: { status?: string | null }) => (p.status || 'aktiv') !== 'inaktiv');
}

// ── Stundenlohn (nur Personal/Geschäftsführung) ──────────────────────────────
// Eigene Tabelle statt Spalte in der Personalakte: die darf der Mitarbeiter selbst
// lesen, den Lohn soll er nicht sehen. Ein leeres Ergebnis heißt hier also auch
// „darf ich nicht sehen" — die RLS meldet keinen Fehler, sie liefert null Zeilen.
export async function ladeVerguetung(): Promise<Record<string, number>> {
  const { data, error } = await supabase.from('personal_verguetung').select('mitarbeiter_id, stundenlohn');
  if (error) { console.error('Vergütung laden fehlgeschlagen', error.message); return {}; }
  const raus: Record<string, number> = {};
  for (const z of data || []) {
    const wert = Number((z as { stundenlohn: unknown }).stundenlohn);
    if (Number.isFinite(wert)) raus[(z as { mitarbeiter_id: string }).mitarbeiter_id] = wert;
  }
  return raus;
}

/** Stundenlohn setzen; null löscht den hinterlegten Satz. */
export async function verguetungSpeichern(mitarbeiterId: string, stundenlohn: number | null): Promise<boolean> {
  const { error } = await supabase.from('personal_verguetung')
    .upsert({ mitarbeiter_id: mitarbeiterId, stundenlohn }, { onConflict: 'mitarbeiter_id' });
  if (error) { console.error('Vergütung speichern fehlgeschlagen', error.message); return false; }
  return true;
}

export async function zuordnungSpeichern(args: {
  mitarbeiterId: string;
  modellId: string;
  gueltigAb: string;
  startSaldoMinuten: number;
  /** Persönliche Zeiten je Wochentag; sie haben Vorrang vor dem Modell. */
  sollMinuten?: Record<string, number> | null;
  /** Abweichender Urlaubsanspruch; null = der des Modells gilt. */
  urlaubstageJahr?: number | null;
  /** Abrechnung nach Stunden — kein festes Soll (#536). */
  nachStunden?: boolean;
}): Promise<boolean> {
  const { error } = await supabase.from('mitarbeiter_arbeitszeit').upsert({
    mitarbeiter_id: args.mitarbeiterId,
    modell_id: args.modellId,
    gueltig_ab: args.gueltigAb,
    start_saldo_minuten: args.startSaldoMinuten,
    soll_minuten: args.sollMinuten ?? null,
    urlaubstage_jahr: args.urlaubstageJahr ?? null,
    nach_stunden: args.nachStunden ?? false,
  }, { onConflict: 'mitarbeiter_id,gueltig_ab' });
  if (error) { console.error('Zuordnung speichern fehlgeschlagen', error.message); return false; }
  return true;
}

// ── Abwesenheiten (Etappe 2) ─────────────────────────────────────────────────

const ABW_SPALTEN = 'id, mitarbeiter_id, typ, von_tag, bis_tag, halber_tag, grund, status, entschieden_von, entschieden_am, entscheidung_kommentar, nachweis_path, nachweis_name, freigabe_stufe, created_at';
const NACHWEIS_BUCKET = 'abwesenheit-nachweise';

/** Abwesenheiten eines Mitarbeiters (ohne Angabe: alle — für Teamkalender und Genehmigung). */
export async function ladeAbwesenheiten(mitarbeiterId?: string | null, abTag?: string): Promise<Abwesenheit[]> {
  let q = supabase.from('abwesenheiten').select(ABW_SPALTEN).order('von_tag', { ascending: false });
  if (mitarbeiterId) q = q.eq('mitarbeiter_id', mitarbeiterId);
  if (abTag) q = q.gte('bis_tag', abTag);
  const { data, error } = await q;
  if (error) { console.error('Abwesenheiten laden fehlgeschlagen', error.message); return []; }
  return (data || []) as Abwesenheit[];
}

export async function antragStellen(args: {
  mitarbeiterId: string;
  typ: AbwesenheitTyp;
  vonTag: string;
  bisTag: string;
  halberTag: boolean;
  grund?: string | null;
  nachweis?: { path: string; name: string } | null;
}): Promise<boolean> {
  const { error } = await supabase.from('abwesenheiten').insert({
    mitarbeiter_id: args.mitarbeiterId,
    typ: args.typ,
    von_tag: args.vonTag,
    bis_tag: args.bisTag,
    halber_tag: args.halberTag,
    grund: args.grund || null,
    nachweis_path: args.nachweis?.path || null,
    nachweis_name: args.nachweis?.name || null,
  });
  if (error) { console.error('Antrag anlegen fehlgeschlagen', error.message); return false; }
  return true;
}

/** Entscheiden — Berechtigung und Stempel setzt ein Trigger in der Datenbank. */
export async function antragEntscheiden(id: string, status: 'genehmigt' | 'abgelehnt', kommentar?: string): Promise<string | null> {
  const { error } = await supabase
    .from('abwesenheiten')
    .update({ status, entscheidung_kommentar: kommentar || null })
    .eq('id', id);
  return error ? error.message : null;
}

export async function antragStornieren(id: string): Promise<boolean> {
  const { error } = await supabase.from('abwesenheiten').update({ status: 'storniert' }).eq('id', id);
  if (error) { console.error('Stornieren fehlgeschlagen', error.message); return false; }
  return true;
}

export async function nachweisHochladen(meineId: string, datei: File): Promise<{ ok: true; path: string; name: string } | { ok: false; fehler: string }> {
  if (datei.size > 10 * 1024 * 1024) return { ok: false, fehler: 'Die Datei ist größer als 10 MB.' };
  const endung = (datei.name.split('.').pop() || 'pdf').toLowerCase().slice(0, 5);
  const path = `${meineId}/${crypto.randomUUID()}.${endung}`;
  const { error } = await supabase.storage.from(NACHWEIS_BUCKET).upload(path, datei, {
    contentType: datei.type || 'application/pdf',
  });
  if (error) return { ok: false, fehler: `Hochladen fehlgeschlagen: ${error.message}` };
  return { ok: true, path, name: datei.name };
}

export async function nachweisUrl(path: string, dateiname?: string | null): Promise<string | null> {
  const { data, error } = await supabase.storage.from(NACHWEIS_BUCKET)
    .createSignedUrl(path, 3600, dateiname ? { download: dateiname } : undefined);
  if (error || !data?.signedUrl) { console.error('Nachweis-Link fehlgeschlagen', error?.message); return null; }
  return data.signedUrl;
}

// ── Ticketzeiten (Etappe 3) ──────────────────────────────────────────────────

const TZ_SPALTEN = 'id, ticket_id, mitarbeiter_id, beginn, ende, taetigkeit, abrechenbar, objekt_id, quelle, created_at';

export async function ladeTicketZeiten(ticketId: string): Promise<TicketZeit[]> {
  const { data, error } = await supabase
    .from('ticket_zeiten').select(TZ_SPALTEN).eq('ticket_id', ticketId).order('beginn', { ascending: false });
  if (error) { console.error('Ticketzeiten laden fehlgeschlagen', error.message); return []; }
  return (data || []) as TicketZeit[];
}

/** Alle Buchungen eines Zeitraums — Grundlage der Auswertung. */
export async function ladeTicketZeitenZeitraum(vonTag: string, bisTag: string, mitarbeiterId?: string | null): Promise<TicketZeit[]> {
  let q = supabase.from('ticket_zeiten').select(TZ_SPALTEN)
    .gte('beginn', `${vonTag}T00:00:00`).lte('beginn', `${bisTag}T23:59:59`)
    .order('beginn', { ascending: false });
  if (mitarbeiterId) q = q.eq('mitarbeiter_id', mitarbeiterId);
  const { data, error } = await q;
  if (error) { console.error('Ticketzeiten laden fehlgeschlagen', error.message); return []; }
  return (data || []) as TicketZeit[];
}

/** Der laufende Ticket-Timer dieses Mitarbeiters (höchstens einer). */
export async function laufenderTicketTimer(mitarbeiterId: string): Promise<TicketZeit | null> {
  const { data, error } = await supabase
    .from('ticket_zeiten').select(TZ_SPALTEN).eq('mitarbeiter_id', mitarbeiterId).is('ende', null).maybeSingle();
  if (error) { console.error('Timer laden fehlgeschlagen', error.message); return null; }
  return (data as TicketZeit) || null;
}

/**
 * Wie `laufenderTicketTimer`, zusätzlich mit Nummer und Titel des Tickets — die
 * Kopfleiste zeigt beides an und braucht sonst einen zweiten Abruf je Sekundentakt.
 * Bewusst zwei Abfragen statt eines Embeds: die Anzeige darf nicht davon abhängen,
 * ob PostgREST den Fremdschlüssel gerade kennt (sonst bliebe der Chip still leer).
 */
export async function laufenderTicketTimerMitTicket(
  mitarbeiterId: string,
): Promise<{ zeit: TicketZeit; ticketNummer: string | null; ticketTitel: string | null } | null> {
  const zeit = await laufenderTicketTimer(mitarbeiterId);
  if (!zeit) return null;
  const { data, error } = await supabase
    .from('tickets').select('ticket_nummer, titel').eq('id', zeit.ticket_id).maybeSingle();
  if (error) console.error('Ticket zum Timer laden fehlgeschlagen', error.message);
  return {
    zeit,
    ticketNummer: (data?.ticket_nummer as string | null) ?? null,
    ticketTitel: (data?.titel as string | null) ?? null,
  };
}

export async function timerStarten(ticketId: string, mitarbeiterId: string, abrechenbar: boolean): Promise<string | null> {
  const { error } = await supabase.from('ticket_zeiten').insert({
    ticket_id: ticketId, mitarbeiter_id: mitarbeiterId, beginn: new Date().toISOString(),
    abrechenbar, quelle: 'timer',
  });
  // Der eindeutige Teilindex meldet sich, wenn anderswo schon ein Timer läuft.
  if (error) return error.code === '23505' ? 'Es läuft bereits ein Timer auf einem anderen Ticket.' : error.message;
  return null;
}

export async function timerStoppen(id: string, taetigkeit: string): Promise<boolean> {
  const { error } = await supabase.from('ticket_zeiten')
    .update({ ende: new Date().toISOString(), taetigkeit: taetigkeit.trim() || null }).eq('id', id);
  if (error) { console.error('Timer stoppen fehlgeschlagen', error.message); return false; }
  return true;
}

export async function ticketZeitSpeichern(args: {
  id?: string;
  ticketId: string;
  mitarbeiterId: string;
  beginn: string;
  ende: string;
  taetigkeit: string;
  abrechenbar: boolean;
}): Promise<boolean> {
  const satz = {
    ticket_id: args.ticketId, mitarbeiter_id: args.mitarbeiterId,
    beginn: args.beginn, ende: args.ende,
    taetigkeit: args.taetigkeit.trim() || null, abrechenbar: args.abrechenbar,
    quelle: 'manuell',
  };
  const { error } = args.id
    ? await supabase.from('ticket_zeiten').update(satz).eq('id', args.id)
    : await supabase.from('ticket_zeiten').insert(satz);
  if (error) { console.error('Ticketzeit speichern fehlgeschlagen', error.message); return false; }
  return true;
}

export async function ticketZeitLoeschen(id: string): Promise<boolean> {
  const { error } = await supabase.from('ticket_zeiten').delete().eq('id', id);
  if (error) { console.error('Ticketzeit löschen fehlgeschlagen', error.message); return false; }
  return true;
}

// ── Monatsabschluss und Auswertung (Etappe 5) ────────────────────────────────

export interface Monatsabschluss {
  id: string;
  mitarbeiter_id: string;
  monat: string;
  ist_minuten: number;
  soll_minuten: number;
  saldo_minuten: number;
  nacht_minuten: number;
  sonntag_minuten: number;
  feiertag_minuten: number;
  urlaubstage: number;
  kranktage: number;
  abgeschlossen_am: string;
  abgeschlossen_von: string | null;
}

export async function ladeAbschluesse(monat: string): Promise<Monatsabschluss[]> {
  const { data, error } = await supabase.from('zeit_monatsabschluss').select('*').eq('monat', monat);
  if (error) { console.error('Abschlüsse laden fehlgeschlagen', error.message); return []; }
  return (data || []) as Monatsabschluss[];
}

export async function monatAbschliessen(werte: {
  mitarbeiterId: string; monat: string; istMinuten: number; sollMinuten: number; saldoMinuten: number;
  nachtMinuten: number; sonntagMinuten: number; feiertagMinuten: number; urlaubstage: number; kranktage: number;
  abgeschlossenVon: string;
}): Promise<string | null> {
  const { error } = await supabase.from('zeit_monatsabschluss').insert({
    mitarbeiter_id: werte.mitarbeiterId, monat: werte.monat,
    ist_minuten: Math.round(werte.istMinuten), soll_minuten: Math.round(werte.sollMinuten),
    saldo_minuten: Math.round(werte.saldoMinuten), nacht_minuten: Math.round(werte.nachtMinuten),
    sonntag_minuten: Math.round(werte.sonntagMinuten), feiertag_minuten: Math.round(werte.feiertagMinuten),
    urlaubstage: werte.urlaubstage, kranktage: werte.kranktage,
    abgeschlossen_von: werte.abgeschlossenVon,
  });
  return error ? error.message : null;
}

export async function abschlussAufheben(id: string): Promise<string | null> {
  const { error } = await supabase.from('zeit_monatsabschluss').delete().eq('id', id);
  return error ? error.message : null;
}

// ── Fahrten und Kilometer ────────────────────────────────────────────────────

export interface Fahrt {
  id: string;
  mitarbeiter_id: string;
  ticket_id: string | null;
  objekt_id: string | null;
  datum: string;
  von_ort: string | null;
  nach_ort: string | null;
  kilometer: number;
  fahrzeug: string | null;
  zweck: string | null;
  /** Reine Fahrtzeit in Minuten (getrennt auswertbar) */
  minuten?: number | null;
  created_at?: string;
}

const FAHRT_SPALTEN = 'id, mitarbeiter_id, ticket_id, objekt_id, datum, von_ort, nach_ort, kilometer, fahrzeug, zweck, minuten, created_at';

export async function ladeFahrten(args: {
  ticketId?: string | null; mitarbeiterId?: string | null; vonTag?: string; bisTag?: string;
}): Promise<Fahrt[]> {
  let q = supabase.from('fahrten').select(FAHRT_SPALTEN).order('datum', { ascending: false });
  if (args.ticketId) q = q.eq('ticket_id', args.ticketId);
  if (args.mitarbeiterId) q = q.eq('mitarbeiter_id', args.mitarbeiterId);
  if (args.vonTag) q = q.gte('datum', args.vonTag);
  if (args.bisTag) q = q.lte('datum', args.bisTag);
  const { data, error } = await q;
  if (error) { console.error('Fahrten laden fehlgeschlagen', error.message); return []; }
  return (data || []) as Fahrt[];
}

export async function fahrtSpeichern(args: {
  id?: string; mitarbeiterId: string; ticketId?: string | null; datum: string;
  vonOrt?: string | null; nachOrt?: string | null; kilometer: number; fahrzeug?: string | null;
  zweck?: string | null; minuten?: number | null;
}): Promise<string | null> {
  const satz = {
    mitarbeiter_id: args.mitarbeiterId,
    ticket_id: args.ticketId || null,
    datum: args.datum,
    von_ort: args.vonOrt || null,
    nach_ort: args.nachOrt || null,
    kilometer: args.kilometer,
    fahrzeug: args.fahrzeug || null,
    zweck: args.zweck || null,
    minuten: args.minuten ?? null,
  };
  const { error } = args.id
    ? await supabase.from('fahrten').update(satz).eq('id', args.id)
    : await supabase.from('fahrten').insert(satz);
  return error ? error.message : null;
}

export async function fahrtLoeschen(id: string): Promise<boolean> {
  const { error } = await supabase.from('fahrten').delete().eq('id', id);
  return !error;
}

/** Bisher verwendete Fahrzeuge — Vorschlagsliste statt eigener Verwaltung. */
export async function ladeFahrzeuge(): Promise<string[]> {
  const { data } = await supabase.from('fahrten').select('fahrzeug').not('fahrzeug', 'is', null).limit(500);
  return [...new Set((data || []).map((f: { fahrzeug: string }) => f.fahrzeug).filter(Boolean))].sort();
}

// ── Korrekturanträge ─────────────────────────────────────────────────────────

export interface Korrektur {
  id: string;
  mitarbeiter_id: string;
  stempel_id: string | null;
  art: 'neu' | 'aendern' | 'loeschen';
  beginn: string | null;
  ende: string | null;
  pause_minuten: number;
  begruendung: string;
  status: 'offen' | 'genehmigt' | 'abgelehnt' | 'zurueckgezogen';
  entschieden_am: string | null;
  entscheidung_kommentar: string | null;
  created_at: string;
}

export async function ladeKorrekturen(mitarbeiterId?: string | null, nurOffen = false): Promise<Korrektur[]> {
  let q = supabase.from('zeit_korrekturen').select('*').order('created_at', { ascending: false });
  if (mitarbeiterId) q = q.eq('mitarbeiter_id', mitarbeiterId);
  if (nurOffen) q = q.eq('status', 'offen');
  const { data, error } = await q;
  if (error) { console.error('Korrekturen laden fehlgeschlagen', error.message); return []; }
  return (data || []) as Korrektur[];
}

export async function korrekturBeantragen(args: {
  mitarbeiterId: string; art: 'neu' | 'aendern' | 'loeschen'; stempelId?: string | null;
  beginn?: string | null; ende?: string | null; pauseMinuten?: number; begruendung: string;
}): Promise<string | null> {
  if (!args.begruendung.trim()) return 'Bitte eine Begründung angeben.';
  const { error } = await supabase.from('zeit_korrekturen').insert({
    mitarbeiter_id: args.mitarbeiterId, art: args.art, stempel_id: args.stempelId || null,
    beginn: args.beginn || null, ende: args.ende || null,
    pause_minuten: args.pauseMinuten ?? 0, begruendung: args.begruendung.trim(),
  });
  return error ? error.message : null;
}

/** Entscheiden läuft über eine Datenbankfunktion — sie schreibt die Zeit gleich mit. */
export async function korrekturEntscheiden(id: string, status: 'genehmigt' | 'abgelehnt', kommentar?: string): Promise<string | null> {
  const { data, error } = await supabase.rpc('zeit_korrektur_entscheiden', {
    korrektur_id: id, neuer_status: status, kommentar: kommentar || null,
  });
  if (error) return error.message;
  return (data as string | null) || null;
}

export async function korrekturZurueckziehen(id: string): Promise<boolean> {
  const { error } = await supabase.from('zeit_korrekturen').update({ status: 'zurueckgezogen' }).eq('id', id);
  return !error;
}

// ── Überstunden: Genehmigung und Auszahlung ──────────────────────────────────

export interface UeberstundenAntrag {
  id: string;
  mitarbeiter_id: string;
  datum: string;
  minuten: number;
  grund: string;
  status: 'offen' | 'genehmigt' | 'abgelehnt' | 'zurueckgezogen';
  entscheidung_kommentar: string | null;
  created_at: string;
}

export async function ladeUeberstundenAntraege(mitarbeiterId?: string | null): Promise<UeberstundenAntrag[]> {
  let q = supabase.from('ueberstunden_antraege').select('*').order('datum', { ascending: false });
  if (mitarbeiterId) q = q.eq('mitarbeiter_id', mitarbeiterId);
  const { data, error } = await q;
  if (error) { console.error('Mehrarbeit laden fehlgeschlagen', error.message); return []; }
  return (data || []) as UeberstundenAntrag[];
}

export async function mehrarbeitBeantragen(args: {
  mitarbeiterId: string; datum: string; minuten: number; grund: string;
}): Promise<string | null> {
  if (!args.grund.trim()) return 'Bitte einen Grund angeben.';
  if (args.minuten <= 0) return 'Bitte die Dauer angeben.';
  const { error } = await supabase.from('ueberstunden_antraege').insert({
    mitarbeiter_id: args.mitarbeiterId, datum: args.datum, minuten: args.minuten, grund: args.grund.trim(),
  });
  return error ? error.message : null;
}

/** Entscheiden — Berechtigung und Stempel setzt ein Trigger. */
export async function mehrarbeitEntscheiden(id: string, status: 'genehmigt' | 'abgelehnt', kommentar?: string): Promise<string | null> {
  const { error } = await supabase.from('ueberstunden_antraege')
    .update({ status, entscheidung_kommentar: kommentar || null }).eq('id', id);
  return error ? error.message : null;
}

export interface Auszahlung {
  id: string;
  mitarbeiter_id: string;
  datum: string;
  minuten: number;
  stundensatz: number | null;
  bemerkung: string | null;
}

export async function ladeAuszahlungen(mitarbeiterId?: string | null): Promise<Auszahlung[]> {
  let q = supabase.from('ueberstunden_auszahlungen').select('*').order('datum', { ascending: false });
  if (mitarbeiterId) q = q.eq('mitarbeiter_id', mitarbeiterId);
  const { data, error } = await q;
  if (error) { console.error('Auszahlungen laden fehlgeschlagen', error.message); return []; }
  return (data || []) as Auszahlung[];
}

export async function auszahlungErfassen(args: {
  mitarbeiterId: string; minuten: number; datum: string; stundensatz?: number | null;
  bemerkung?: string | null; vonId: string;
}): Promise<string | null> {
  if (args.minuten <= 0) return 'Bitte die Stundenzahl angeben.';
  const { error } = await supabase.from('ueberstunden_auszahlungen').insert({
    mitarbeiter_id: args.mitarbeiterId, minuten: args.minuten, datum: args.datum,
    stundensatz: args.stundensatz ?? null, bemerkung: args.bemerkung || null, erfasst_von: args.vonId,
  });
  return error ? error.message : null;
}

export async function auszahlungLoeschen(id: string): Promise<string | null> {
  const { error } = await supabase.from('ueberstunden_auszahlungen').delete().eq('id', id);
  return error ? error.message : null;
}

// ── Genehmigungsverlauf und Kommentare ───────────────────────────────────────

export interface VerlaufEintrag {
  id: string;
  antrag_typ: 'abwesenheit' | 'korrektur' | 'ueberstunden';
  antrag_id: string;
  aktion: 'gestellt' | 'kommentar' | 'genehmigt' | 'abgelehnt' | 'storniert';
  stufe: number | null;
  autor_id: string | null;
  kommentar: string | null;
  created_at: string;
}

export async function ladeVerlauf(typ: VerlaufEintrag['antrag_typ'], antragId: string): Promise<VerlaufEintrag[]> {
  const { data, error } = await supabase
    .from('antrag_verlauf').select('*')
    .eq('antrag_typ', typ).eq('antrag_id', antragId)
    .order('created_at');
  if (error) { console.error('Verlauf laden fehlgeschlagen', error.message); return []; }
  return (data || []) as VerlaufEintrag[];
}

export async function kommentarSchreiben(
  typ: VerlaufEintrag['antrag_typ'], antragId: string, autorId: string, text: string,
): Promise<string | null> {
  if (!text.trim()) return 'Bitte einen Text eingeben.';
  const { error } = await supabase.from('antrag_verlauf').insert({
    antrag_typ: typ, antrag_id: antragId, aktion: 'kommentar', autor_id: autorId, kommentar: text.trim(),
  });
  return error ? error.message : null;
}

/** Freigabestufen (1–3) — gilt für Abwesenheiten. */
export async function ladeGenehmigungsstufen(): Promise<number> {
  const { data } = await supabase.from('zeit_einstellungen').select('genehmigung_stufen').maybeSingle();
  return data?.genehmigung_stufen ?? 1;
}

export async function setzeGenehmigungsstufen(stufen: number): Promise<string | null> {
  const { error } = await supabase.from('zeit_einstellungen').update({ genehmigung_stufen: stufen }).eq('id', true);
  return error ? error.message : null;
}

// ── Lohnarten und DATEV-Einstellungen ────────────────────────────────────────

export async function ladeLohnarten(): Promise<Lohnart[]> {
  const { data, error } = await supabase.from('lohnarten').select('*').order('schluessel');
  if (error) { console.error('Lohnarten laden fehlgeschlagen', error.message); return []; }
  return (data || []) as Lohnart[];
}

export async function lohnartSpeichern(schluessel: string, nummer: string, aktiv: boolean): Promise<string | null> {
  const { error } = await supabase.from('lohnarten')
    .update({ nummer: nummer.trim() || null, aktiv }).eq('schluessel', schluessel);
  return error ? error.message : null;
}

export async function ladeDatevEinstellungen(): Promise<{ beraternummer: string | null; mandantennummer: string | null }> {
  const { data } = await supabase.from('zeit_einstellungen')
    .select('datev_beraternummer, datev_mandantennummer').maybeSingle();
  return { beraternummer: data?.datev_beraternummer || null, mandantennummer: data?.datev_mandantennummer || null };
}

export async function setzeDatevEinstellungen(beraternummer: string, mandantennummer: string): Promise<string | null> {
  const { error } = await supabase.from('zeit_einstellungen')
    .update({ datev_beraternummer: beraternummer.trim() || null, datev_mandantennummer: mandantennummer.trim() || null })
    .eq('id', true);
  return error ? error.message : null;
}

/** Personalnummern aus der Personalakte — Lodas verlangt sie je Zeile. */
export async function ladePersonalnummern(): Promise<Record<string, string>> {
  const { data } = await supabase.from('personalakte').select('mitarbeiter_id, personalnummer');
  return Object.fromEntries(
    (data || []).filter((p: any) => p.personalnummer).map((p: any) => [p.mitarbeiter_id, p.personalnummer]),
  );
}
